import React, { useState } from 'react';
import { styled } from '@linaria/react';
import { useTheme, ITheme } from 'src/theme';
import { css } from '@linaria/core';

import IconInfo from '@assets/icon/information-red.svg';
import IconClose from '@assets/icon/circle-cancel-red.svg';
import Link from '@components/Link';
import ContainerBlock from '@components/ContainerBlock';
import Icon from '@components/Icon';
import Text from '@components/Text';

const Container = styled.div<{ theme: ITheme }>`
  width: 100%;
  overflow: auto;
  &::-webkit-scrollbar {
    width: 13px;
    height: 13px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${props => props.theme?.colors.grey[1000]};
    border-radius: 20px;
  } 
  &::-webkit-scrollbar-thumb {
    height: 120px;
    border-radius: 20px;
    border: 2px solid ${props => props.theme?.colors.grey[1000]};
    background-color: ${props => props.theme?.colors.secondary};
    background-clip: padding-box;
  }
`

const TableContainer = styled.table<StyledProps>`
  border: 0px solid #CCC;
  border-collapse: collapse;
  width: 100%;
	margin: 20px 0;
	table-layout: fixed;
`;

const TR = styled.tr<StyledProps>`
  padding: 8px 20px;
  border: none;
  background-color: ${props => props.type === 'header' ?
		props.theme.colors.primary :
		(props.type === 'row-odd' ? props.theme.colors.grey[600] : props.theme.colors.white)
	};

`
const TH = styled.th<StyledProps>`
  color: ${props => props.theme.colors.white};
  white-space: nowrap;
  font-size: ${props => props.theme.fontSize.text};
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  padding: 8px 15px 8px 0px;
  border: none;
  text-align: left;
	padding-left: 21px;
	padding-right: 21px;
  &:first-child {
    padding-left: 21px;
  }
  @media screen and (min-width: 768px) {
    text-align: left;
  }

`
const TD = styled.td<StyledProps>`
  font-size: ${props => props.theme.fontSize.text};
  font-weight: ${props => props.isKey ? 'bold' : 'normal'};
  font-stretch: normal;
  font-style: normal;
  line-height: 20px;
  letter-spacing: -0.1px;
  white-space: nowrap;
  padding: 12px 0;
  color: ${props => props.theme.colors.grey[500]};
  border: none;
  &:first-child {
    padding-left: 21px;
		padding-right: 21px;
  }
	&:last-child {
		padding-left: 21px;
		padding-right: 21px;
  }
`

const CloseButton = styled.div`
		display: flex;
		font-weight: bold;
		cursor: pointer;
	`
const textwWrap = css`
	white-space: pre-wrap;
`


interface StyledProps {
	theme: ITheme,
	type?: 'header' | 'row-odd' | 'row-even',
	isKey?: boolean;
}

export interface TableProps {
	theme?: ITheme,
	header: any[];
	source: any[];
}

function PlanTable({ header }: TableProps) {
	const theme = useTheme();
	const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

	const onChangeTooltip = () => {
		setTooltipOpen(!tooltipOpen);
	}

	const renderHeader = () => {
		return (
			<TR theme={theme} type="header">
				<TH theme={theme}>Singlife CareShield Standard</TH>
				<TH theme={theme}>Singlife CareShield Plus</TH>
			</TR>
		);
	};

	const renderBody = () => {
		return (
			<React.Fragment>
				<TR theme={theme}>
					<TD theme={theme} colSpan={2}>
						
							<strong>Severe disability definition</strong>
							{!tooltipOpen && (<Link className="tooltip" icon={IconInfo} iconSize="md" text="" variant="button" onClick={onChangeTooltip} />)}
							{tooltipOpen && (<Link className="tooltip-visible" icon={IconClose} iconSize="md" text="" variant="button" onClick={onChangeTooltip} />)}
							{tooltipOpen && (
								<ContainerBlock className="tooltip" padding={20} paddingTablet={20} margin="15px 0 0 0" background={theme.colors.grey[600]}>
									<Text margin={"0px 0 12px 0"} className={textwWrap}>Inability to perform a defined number of Activities of Daily Living (ADL). The 6 ADL are Dressing, Washing, Toileting, Walking or Moving Around, Transferring and Feeding.</Text>
									<CloseButton onClick={onChangeTooltip}> <Icon name="cancel-red" className='close-red' size="xsm" /> <Text fontWeight="bold" color={theme.colors.primary}> &nbsp;Close</Text>  </CloseButton>
								</ContainerBlock>
							)}
						
					</TD>
				</TR>
				<TR theme={theme}>
					<TD theme={theme}>
						<Text className={textwWrap}>Inability to perform at least <strong>3 out of 6</strong> ADL</Text>
					</TD>
					<TD theme={theme}>
						<Text className={textwWrap}>Inability to perform at least <strong>2 out of 6</strong> ADL</Text>
					</TD>
				</TR>
				<TR type={'row-odd'} theme={theme}>
					<TD theme={theme}>
						<strong>Monthly benefit duration</strong><br/><br/>
						Lifetime
					</TD>
					<TD theme={theme}><strong>&nbsp;</strong><br/><br/>Lifetime</TD>
				</TR>
				<TR theme={theme}>
					<TD theme={theme} colSpan={2}>
						<strong>Monthly benefit amount</strong><br/><br/>
						<Text className={textwWrap}>S$200 to S$5,000 per month (in increments of S$100) on top of CareShield Life&apos;s payout</Text>
					</TD>
				</TR>

			</React.Fragment>
		);
	}
	return (
		<Container theme={theme}>
			<TableContainer width={600} theme={theme}>
				<thead>
					{renderHeader()}
				</thead>
				<tbody>
					{renderBody()}
				</tbody>
			</TableContainer>
		</Container>

	);
};

PlanTable.defaultProps = {
	header: [],
	source: []
}

export default PlanTable;
